import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

import { ApiService } from '../api/api.service';
import { IEntity, IEntityCache, TimeMode } from '../inputs.datastore/types';
import { AGENT_MODEL, Device, DeviceType, IDeviceData } from './device';
import { filter, find, findIndex, get, isArray, isString, remove, toLower } from 'lodash';
import { TimestampRange } from '../../components/time-picker/types';
import { BenchmarkService } from '../../services/benchmark/benchmark.service';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root'
})
export class DevicesService implements IEntityCache {
  realtimemonitorcache: Array<Device> = [];
  cache = new BehaviorSubject<Array<Device>>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  firmwareCache = new BehaviorSubject<any>([]);
  mtsAgentModel = [
    'TB/MTS-5800-100G',
    'TB/MTS-5800v2',
    'MTS-5882',
    'SC4800',
    'SC4800P',
    'TB-5800v2',
    'MTS-5800v2',
    'TB-5882',
    'TB-5800-100G',
    'MTS-5800-100G'
  ];
  reflectorsDeviceCache = new BehaviorSubject<Array<Device>>([]);

  getDevicesPath = '/vibe/inventory/v3/queryDevices';
  agentBuiderPath = '/agent';
  getAgentinfoPath = '/agent/v1/getGenericConfig';
  generateAgentLogs = '/agent/v1/generatelogs';
  getAgentLogsDetails = '/agent/v1/getlogs';
  updated: Subject<Array<Device>>;
  queryLoading = false;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly apiService: ApiService,
    private readonly benchmarkService: BenchmarkService,
    private readonly userService: UserService
  ) {}

  getEntityName(): string {
    return 'device';
  }

  getInterfaces(devices: Array<Device>, id: string): Device[] {
    return devices.filter(
      device => device.getParentId() === id && device.getParamByName('type')?.value === 'interface'
    );
  }

  findById(id: string): IEntity {
    return this.getDeviceById(id);
  }

  isMtsAgentModel(agentType: string): boolean {
    return this.mtsAgentModel.some(item => item?.toLowerCase() === agentType?.toLowerCase());
  }

  findByName(name: string): IEntity {
    return find(this.getDevices(), site => site.getName() === name);
  }

  getByName(name: string): IEntity[] {
    return filter(this.getDevices(), site => site.getName() === name);
  }

  findByEscapedName(name: string): Device {
    return find(this.getDevices(), app => app.getEscapedName() === name);
  }

  getByEscapedName(name: string): IEntity[] {
    name = toLower(name);
    // we need to support case insensitive match
    return filter(this.getDevices(), app => toLower(app.getEscapedName()) === name);
  }

  getDeviceName(name: string): Device {
    return find(this.getDevices(), device => device.getDeviceName() === name);
  }

  findDeviceByID(deviceId: string): Device {
    for (const device of this.getDevices()) {
      if (device.getId() === deviceId) {
        return device;
      }
    }
  }

  findDeviceByUniqueName(deviceName: string): Device {
    for (const device of this.getDevices()) {
      if (device.getUniqueName() === deviceName) {
        return device;
      }
    }
  }

  /**
   * An ID field from database may look like this:
   * "id": "NjE2YmE0ZjZiODZiNGU5NDg0M2Q0NzZkOTJmYTg4NjYxNTY3MDAyNjY5ODAy"
   * "id": "1301"
   * "id": "1170D4B1-7672-32CD-8D7E-7C677B998407|1301"
   * "id": "dba64156-1363-4d71-9d33-1c2537e303cd"
   * Only the last example is something that can be found directly in combinedflow/deviceid column.
   * For all other cases, we have to take given input parameter and match either against ipaddress column,
   * or split internal device id (along the | character) and only then do comparison.
   * @param id
   */
  getDeviceById(id: string): Device {
    return find(this.getDevices(), item => {
      // simplest case - direct match
      if (item.getId() === id) {
        return true;
      }
      // 2nd simplest case
      if (item.getIpAddress() === id) {
        return true;
      }
      if (item.getMgmtIp() === id) {
        return true;
      }
      if (item.getId()) {
        const lastHope = item.getId().split('|')[0];
        if (lastHope === id) {
          return true;
        }
      }
      return false;
    });
  }

  findDeviceByUniqueId(deviceId: string): Device {
    for (const device of this.getDevices()) {
      if (device.getUniqueId() === deviceId) {
        return device;
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValues(): any[] {
    return this.getDevices();
  }

  getUniqueValues(): any[] {
    return this.removeDuplicateDeviceObjects(this.getDevices());
  }

  // eslint-disable-next-line
  getFirmwareValues(): any {
    return this.firmwareCache;
  }

  removeDuplicateDeviceObjects(deviceObj: Device[]): Device[] {
    return deviceObj.filter((device, index) => {
      return (
        index === deviceObj.findIndex(o => device.deviceData.uniqueId === o.deviceData.uniqueId)
      );
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValuesAsync(): Observable<Array<any>> {
    const apps = this.getValues();
    if (!apps || apps.length === 0) {
      return this.updateDevices(this.realtimemonitorcache).pipe(first());
    }
    return of(apps).pipe(first());
  }

  getDevices(): Array<Device> {
    return this.chooseCacheModel();
  }

  chooseCacheModel(): Device[] {
    // simplified implementation/no support for history mode
    return this.realtimemonitorcache;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getGenericAgentConfigDetail(): Observable<any> {
    const url = `${this.getAgentinfoPath}`;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.get$<any>(url, {});
  }

  updateDevices(
    cache: Array<Device>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    params?: TimestampRange,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    firstRequest?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mode?: TimeMode
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<any> {
    if (this.queryLoading) {
      return this.updated;
    }
    this.updated = new Subject();
    this.queryLoading = true;
    this.benchmarkService.registerPageEvent('deviceQuery');
    if (this.userService.isFusionUIProject()) {
      this.getGenericAgentConfigDetail()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(firmware => {
          this.firmwareCache.next(firmware);
        });
    }
    this.getReflectorDevices();
    this.getDevices$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.queryLoading = false;
          this.benchmarkService.logPageEventTime('Device query', 'deviceQuery');
          this.benchmarkService.registerPageEvent('deviceDataManipulation');
          const responseData = this.transformResponse(response.data);
          this.assignDeviceToDeviceList(cache, responseData);
          this.updated.next(this.getDevices());
          this.benchmarkService.logPageEventTime('Device data', 'deviceDataManipulation');

          return this.chooseCacheModel();
        },
        //eslint-disable-next-line
        error => {
          this.queryLoading = false;
        },
        () => {
          this.queryLoading = false;
          this.updated.complete();
        }
      );
    return this.updated;
  }

  updateCache(): void {
    this.getDevices$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        this.assignDeviceToDeviceList(
          this.realtimemonitorcache,
          this.transformResponse(response.data)
        );
      });
  }

  getReflectorDevices(): void {
    this.getDevices$('twamp-reflector')
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        const filtereData = response.data.filter(data => data.model.model !== AGENT_MODEL.ESAM);
        const transformedData = this.transformResponse(filtereData);
        this.reflectorsDeviceCache.next(transformedData);
      });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  transformResponse(dataIn: any): Array<Device> {
    dataIn = isString(dataIn) ? JSON.parse(dataIn) : dataIn;
    const response = [];
    if (dataIn !== null && isArray(dataIn)) {
      dataIn.forEach(result => {
        if (result.extension?.capabilities?.['phy-list']) {
          result.extension?.capabilities['phy-list'].forEach(phyList => {
            const configIn = {} as IDeviceData;
            configIn.id = phyList['phy-id'] === 'p1' ? result.id + '-port1' : result.id + '-port2';
            configIn.ipaddress = phyList['default-test-ip'];
            configIn.mgmtIPaddress = this.getMgmtInfo(result);
            configIn.uniqueId = result.id;
            configIn.name =
              phyList['phy-id'] === 'p1' ? result.alias + ' - port1' : result.alias + ' - port2';
            configIn.nameId = result.name;
            configIn.deviceName =
              phyList['phy-id'] === 'p1' ? result.name + ' - port1' : result.name + ' - port2';
            configIn.phyId = phyList['phy-id'];
            configIn.phyName = phyList['name'];
            configIn.site = result.extension?.location?.address
              ? Number(result.extension.location.address)
              : null;
            configIn.status = this.handleStatus(result);
            configIn.location = result.extension?.location?.address;
            configIn.substatus = result.substatus;
            configIn.type = 'GigaTestAgent';
            configIn.model = result.model;
            configIn.lastUpgradeMessage = result.lastUpgradeMessage;
            configIn.lastUpdateStartTime = result.lastUpdateStartTime;
            configIn.lastUpgradeMessageTime = result.lastUpgradeMessageTime;
            configIn.capabilities = result.extension?.capabilities;
            configIn.comMode = result.comMode;
            configIn.connections = result.connections ? result.connections : null;
            configIn.activity = result.activity ?? null;
            this.parseQTParams(configIn, result, phyList);

            const listObj: Device = new Device(configIn);

            response.push(listObj);
          });
        } else {
          const configIn = {} as IDeviceData;
          configIn.id = result.id;
          if (result.extension?.capabilities?.phy) {
            configIn.ipaddress = this.getIPAddress(result);
          }
          configIn.mgmtIPaddress = result.model?.model?.includes('JMEP')
            ? result.extension?.capabilities?.phy['assigned-ip']
            : this.getMgmtInfo(result);
          configIn.uniqueId = result.id;
          configIn.name = result.alias;
          configIn.nameId = result.name;
          configIn.deviceName = result.name;
          configIn.phyId = '';
          configIn.phyName = '';
          configIn.site = result.extension?.location?.address
            ? Number(result.extension.location.address)
            : null;
          configIn.status = this.handleStatus(result);
          configIn.location = result.extension?.location?.address;
          configIn.substatus = result.substatus;
          configIn.type = 'GigaTestAgent';
          configIn.model = result.model;
          configIn.lastUpgradeMessage = result.lastUpgradeMessage;
          configIn.lastUpdateStartTime = result.lastUpdateStartTime;
          configIn.lastUpgradeMessageTime = result.lastUpgradeMessageTime;
          configIn.capabilities = result.extension?.capabilities;
          configIn.comMode = result.comMode;
          configIn.connections = result.connections ? result.connections : null;
          configIn.activity = result.activity ?? null;
          this.parseParams(configIn, result);

          const listObj: Device = new Device(configIn);

          response.push(listObj);
        }
      });
      this.enrichInterfaces(response);
    }
    return response;
  }

  //eslint-disable-next-line
  getIPAddress(result: any): string {
    const ipAddress =
      result.extension?.capabilities?.phy['test-ip'] ??
      result.extension?.capabilities?.phy['default-test-ip'];
    if (result.model?.model?.toLowerCase().includes('vpma') || ipAddress === '0.0.0.0') {
      return '';
    }
    return ipAddress;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getMgmtInfo(result: any): any {
    const mgmtInfo = { ip: '127.0.0.1', port: 80 };

    if (result && result.connections && result.connections.length > 0) {
      for (const x in result.connections) {
        // if type does not exist the connection is assumed to be for the probe per the inventory design
        // so use the connection if type does not exist or it specifically states it is for the probe
        if (!result.connections[x].type || result.connections[x].type === 'probe') {
          if (result.connections[x].uri) {
            const parser = result.connections[x].uri.split('//');
            if (parser.length > 1) {
              const pieces = parser[1].split(':');
              if (pieces.length > 0) mgmtInfo.ip = pieces[0];
              if (result['comMode'] === 'firewall' && pieces[1] === '0') {
                mgmtInfo.port = 443;
              } else {
                if (pieces.length > 1) mgmtInfo.port = pieces[1];
              }
            }
          }
          break;
        }
      }
    }
    return mgmtInfo.ip;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  parseParams(configIn: IDeviceData, result: any): void {
    configIn.params = [];
    configIn.params.push({ name: 'platform', value: result.deployment });
    configIn.params.push({ name: 'model', value: result.model?.model });
    configIn.params.push({ name: 'version', value: result.model?.version });
    configIn.params.push({ name: 'mac', value: result.extension?.capabilities?.mac });
    configIn.params.push({ name: 'firstRegistrationTime', value: result.firstRegistrationTime });
    configIn.params.push({ name: 'lastRegistrationTime', value: result?.lastRegistrationTime });
    configIn.params.push({ name: 'lastHeartBeatTime', value: result.lastHeartBeatTime });
    if (result.extension?.capabilities?.phy) {
      configIn.params.push({
        name: 'defaultTestIp',
        value:
          result.extension?.capabilities?.phy['assigned-ip'] ??
          result.extension?.capabilities?.phy['default-test-ip']
      });
      configIn.params.push({
        name: 'localTestIp',
        value:
          result.extension?.capabilities?.phy['local-test-ip'] ??
          result.extension?.capabilities?.phy['management-ip']
      });
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  parseQTParams(configIn: IDeviceData, result: any, phyList: any): void {
    configIn.params = [];
    configIn.params.push({ name: 'platform', value: result.deployment });
    configIn.params.push({ name: 'model', value: result.model?.model });
    configIn.params.push({ name: 'version', value: result.model?.version });
    configIn.params.push({ name: 'mac', value: result.extension?.capabilities?.mac });
    configIn.params.push({ name: 'firstRegistrationTime', value: result.firstRegistrationTime });
    configIn.params.push({ name: 'lastRegistrationTime', value: result?.lastRegistrationTime });
    configIn.params.push({ name: 'lastHeartBeatTime', value: result.lastHeartBeatTime });
    configIn.params.push({
      name: 'defaultTestIp',
      value: phyList['default-test-ip']
    });
    configIn.params.push({
      name: 'localTestIp',
      value: phyList['external-test-ip']
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  handleStatus(result: any): string {
    if (result.state === 'disabled') {
      return result.state;
    } else {
      return result.status;
    }
  }

  clearRealtimeCache(): void {
    this.realtimemonitorcache.length = 0;
  }

  clearTabulatorCache(): void {
    const clearItems = [
      'tabulator-device-selection-group',
      'tabulator-device-selection-filter',
      'tabulator-device-selection-sort',
      'tabulator-device-selection-page'
    ];
    clearItems.forEach(item => localStorage.setItem(item, null));
  }

  enrichInterfaces(devices: Device[]): void {
    for (const device of devices) {
      device.setInterfaces(this.getInterfaces(devices, device.getId()));
    }
  }

  findDeviceCacheByID(cache: Array<Device>, deviceId: string): Device {
    for (const device of cache) {
      if (device.getId() === deviceId) {
        return device;
      }
    }
  }

  assignDeviceToDeviceList(cache: Device[], data: Device[]): void {
    // simplified algo, that simply replaces full cache with new list
    // notice we preserve the array object!
    cache.length = 0;
    for (const entry of data) {
      cache.push(entry);
    }
    this.cache.next(this.chooseCacheModel());
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDevices$(testType = null): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.post$<any>('/uicomm/getDeviceInventory', {
      filter: { testType: testType, testMode: null },
      noRenewSession: true,
      runningList: true
    });
  }
  // eslint-disable-next-line
  getDeviceById$(agentId: string): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.post$<any>(this.getDevicesPath, {
      criteria: { must: [{ exact: { id: agentId } }] }
    });
  }

  // eslint-disable-next-line
  generateAgentLogs$(agentList: any[]): Observable<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.post$<any>(this.generateAgentLogs, {
      agents: agentList
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAgentLogsDetail$(): Observable<any> {
    const url = `${this.getAgentLogsDetails}`;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.get$<any>(url, {});
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  findByType(deviceType: DeviceType): Array<any> {
    return filter(this.getDevices(), { deviceData: { type: deviceType } });
  }

  dispose(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  removeDevicesFromCache(deviceId: string): void {
    const allDevices = this.getDevices();
    this.removeOneDevice(allDevices, deviceId);
    this.cache.next(this.chooseCacheModel());
  }

  removeOneDevice(allDevices: Array<Device>, id: string): void {
    this.removeDeviceCacheByID(allDevices, id);
  }

  removeDeviceCacheByID(cache: Array<Device>, deviceId: string): Array<Device> {
    return remove(cache, device => device.getId() === deviceId);
  }

  updateDeviceStatus(id: string, status: string): void {
    const allDevices = this.getDevices();
    const index = this.findIndexById(allDevices, id);
    if (index !== -1) {
      this.findDeviceByIndex(allDevices, index).setStatus(status);
    }

    this.cache.next(this.chooseCacheModel());
  }

  findIndexById(cache: Array<Device>, uid: string): number {
    return findIndex(cache, device => uid === device.getId());
  }

  findDeviceByIndex(cache: Array<Device>, index: number): Device {
    return get(cache, `[${index}]`, null);
  }
}
